import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

function Group({ title, icon: Icon, items, tone, chipClass, testId }) {
  if (!items.length) return null;
  return (
    <div data-testid={testId}>
      <h2 className="font-display text-lg font-semibold flex items-center gap-2 mb-3">
        <Icon className={`w-5 h-5 ${tone}`} /> {title} ({items.length})
      </h2>
      <div className="space-y-3">
        {items.map((item) => (
          <Link
            key={`${item.section}-${item.id}`}
            to={`/workspace/carer-essentials/${item.section}`}
            className="card-base p-4 flex items-center justify-between gap-4 hover:shadow-md transition-shadow"
            data-testid={`ce-expiry-${item.id}`}
          >
            <div className="min-w-0">
              <p className="font-medium truncate">{item.title}</p>
              <p className="text-sm text-ink-muted">{item.source_label} · Expiry {item.expiry_date}</p>
            </div>
            <span className={`pill shrink-0 ${chipClass}`}>
              {item.days_left < 0 ? `Expired ${Math.abs(item.days_left)}d ago` : `${item.days_left}d left`}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function ExpiriesRenewals() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/api/carer-essentials/expiries")
      .then(({ data }) => setData(data))
      .catch((err) => {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not load expiries");
        setData({ expired: [], expiring_soon: [], future: [] });
      });
  }, []);

  const isEmpty = data && !data.expired.length && !data.expiring_soon.length && !data.future.length;

  return (
    <div className="container-jcc py-8 md:py-12 max-w-3xl" data-testid="ce-section-expiries">
      <Link to="/workspace/carer-essentials" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink" data-testid="ce-back-to-dashboard">
        <ArrowLeft className="w-4 h-4" /> Carer Essentials
      </Link>

      <div className="mt-4">
        <h1 className="font-display text-2xl md:text-3xl font-bold">Expiries &amp; Renewals</h1>
        <p className="mt-1.5 text-ink-muted max-w-xl">
          A single view of everything expiring or expired across your Documents and Training &amp; CPD.
        </p>
      </div>

      <div className="mt-6 space-y-8">
        {data === null && <p className="text-ink-muted">Loading…</p>}
        {isEmpty && (
          <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-ink-muted" data-testid="ce-empty-state">
            Nothing with an expiry date yet — add expiry dates to your documents and CPD entries to track them here.
          </div>
        )}
        {data && !isEmpty && (
          <>
            <Group title="Expired" icon={AlertTriangle} items={data.expired} tone="text-red-600" chipClass="bg-red-50 text-red-700" testId="ce-expiries-expired" />
            <Group title="Expiring within 30 days" icon={Clock} items={data.expiring_soon} tone="text-amber-600" chipClass="bg-amber-50 text-amber-700" testId="ce-expiries-soon" />
            <Group title="Up to date" icon={CheckCircle2} items={data.future} tone="text-teal" chipClass="bg-teal/10 text-teal" testId="ce-expiries-future" />
          </>
        )}
      </div>
    </div>
  );
}
